import { ChangeDetectionStrategy, Component, Input, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { differenceInMinutes, parseISO } from 'date-fns';
import { sort } from 'remeda';
import { BehaviorSubject, combineLatest, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import {
  ActiveDriver,
  ListAvailableDriversResponseDriver,
  LoHiAdminInProgressLoadListItem,
} from 'src/app/types';
import { sortBy, sortByName } from 'src/app/utilities/commonSorters';
import { DriversService } from '../../drivers.service';
import { LoadsForAllProductsService } from '../../loads-for-all-products.service';

interface LoHiAdminInProgressLoadListItemWithPriority extends LoHiAdminInProgressLoadListItem {
  allowShiftBooking?: boolean;
}

interface RegionGroupedAvailableDrivers {
  id: number;
  name: string;
  drivers: ListAvailableDriversResponseDriver[];
}

type DriverSorter = (a: ActiveDriver, b: ActiveDriver) => number;

type AvaDriverSorter = (a: ListAvailableDriversResponseDriver, b: ListAvailableDriversResponseDriver) => number;

interface RegionDetail {
  id: number;
  name: string;
}

@Component({
  selector: 'lohi-driver-list',
  templateUrl: './driver-list.component.html',
  styles: [],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DriverListComponent implements OnInit {
  @ViewChild('updateHOS') public updateHOSModal: TemplateRef<any>;
  public updateHOSFormGroup: FormGroup;
  @Input() public region: RegionDetail;
  public inProgressShiftDrivers$: Observable<ActiveDriver[]>;
  public upcomingShiftDrivers$: Observable<ActiveDriver[]>;
  public shiftsTodayCount$: Observable<number>;
  public needsAttentionShifts$: Observable<ActiveDriver[]>;
  public onShiftOkDrivers$: Observable<ActiveDriver[]>;
  public lateDriverLoads$: Observable<LoHiAdminInProgressLoadListItemWithPriority[]>;
  public inProgressLoadsOffShift$: Observable<LoHiAdminInProgressLoadListItemWithPriority[]>;
  public inProgressLoadsOnShift$: Observable<LoHiAdminInProgressLoadListItemWithPriority[]>;
  public availableDrivers$: Observable<ListAvailableDriversResponseDriver[]>;
  public availableDriversByRegion$: Observable<RegionGroupedAvailableDrivers[]>;
  public shiftFilterDate$: Observable<Date>;
  public hosResetDrivers$: Observable<ActiveDriver[]>;

  public sortByOS = 'driverName';
  public sortReverseOS = false;
  public sortingFunctionOS$$ = new BehaviorSubject<DriverSorter>(getSortBy(this.sortByOS, this.sortReverseOS));

  public sortByUS = 'driverName';
  public sortReverseUS = false;
  public sortingFunctionUS$$ = new BehaviorSubject<DriverSorter>(getSortBy(this.sortByUS, this.sortReverseUS));

  public sortByA = 'name';
  public sortReverseA = false;
  public sortingFunctionA$$ = new BehaviorSubject<AvaDriverSorter>(getSortByAva(this.sortByA, this.sortReverseA));

  public sortByUA = 'driverName';
  public sortReverseUA = false;
  public sortingFunctionUA$$ = new BehaviorSubject<DriverSorter>(getSortBy(this.sortByUA, this.sortReverseUA));

  constructor(
    private loadsService: LoadsForAllProductsService,
    private driverService: DriversService,
    private matDialog: MatDialog,
  ) { }

  public ngOnInit(): void {
    const allowedShiftBookingMap$ = this.driverService.activeDriverList$.pipe(
      map((drivers) => {
        return (drivers || []).reduce((acc, driver) => {
          acc[driver.id] = driver.allowShiftBooking;
          return acc;
        }, {});
      }),
    );
    this.inProgressLoadsOffShift$ = this.loadsService.inProgressLoads$.pipe(
      map((value) => value.filter((value1) => !value1.onShift && value1.partialLoad)),
    );
    this.inProgressLoadsOnShift$ = this.loadsService.inProgressLoads$.pipe(
      map((value) => value.filter((value1) => !value1.onShift && !value1.partialLoad && !value1.minutesLate)),
    );
    this.lateDriverLoads$ = combineLatest([
      allowedShiftBookingMap$,
      this.loadsService.inProgressLoads$.pipe(
        map((value) => value.filter((value1) => !value1.onShift && !value1.partialLoad && value1.minutesLate > 0)),
      ),
    ]).pipe(
      map(([allowedShiftBookingMap, lateLoads]) => {
        return lateLoads.map((load) => {
          const newLoad: LoHiAdminInProgressLoadListItemWithPriority = {
            ...load,
          };
          newLoad.allowShiftBooking = allowedShiftBookingMap[load.driverId] || false;
          return newLoad;
        });
      }),
    );
    this.inProgressShiftDrivers$ = combineLatest([
      this.driverService.dateFilteredActiveDriverList$,
      this.sortingFunctionOS$$.asObservable(),
    ]).pipe(
      map(([value, sortingFunc]) => inProgressFilter(value, sortingFunc)),
      map((value) => value.filter((value1) => value1.inProgressShift.regionId === this.region.id)),
    );
    this.upcomingShiftDrivers$ = combineLatest([
      this.driverService.dateFilteredActiveDriverList$,
      this.sortingFunctionUS$$,
    ]).pipe(
      map(([shifts, sortingFunc]) =>
        upcomingShiftFilter(
          shifts.filter((value1) => value1.upcomingShift?.regionId === this.region.id && !value1.inProgressShift),
          sortingFunc,
        ),
      ),
    );
    this.shiftsTodayCount$ = combineLatest([this.inProgressShiftDrivers$, this.upcomingShiftDrivers$]).pipe(
      map(([inProg = [], upcoming = []]) => {
        return inProg.length + upcoming.length;
      }),
    );
    this.needsAttentionShifts$ = this.inProgressShiftDrivers$.pipe(map(needsAttentionFilter));
    this.onShiftOkDrivers$ = this.inProgressShiftDrivers$.pipe(map(notActionNeededFilter));
    this.shiftFilterDate$ = this.driverService.shiftFilterDate$;
    this.availableDrivers$ = combineLatest([this.driverService.availableDrivers$, this.sortingFunctionA$$]).pipe(
      map(([value, sortFunc]) => {
        return sort(
           (value || []).filter((value1) => value1.lastRegionName === this.region.name),
           sortFunc,
         );
        }),
    );
    this.availableDriversByRegion$ = this.availableDrivers$.pipe(map(toRegionGroupedDrivers));
    this.hosResetDrivers$ = combineLatest([
      this.driverService.activeDriverList$.pipe(
        map((drivers) =>
          drivers.filter(
            (driver) =>
              !driver.inProgressShift && !driver.upcomingShift && driver.previousShift?.regionId === this.region.id,
          ),
        ),
      ),
      this.availableDrivers$,
      this.sortingFunctionUA$$,
    ]).pipe(
      map(([activeDrivers, availableDrivers, sortingFunc]) => {
        const availableMap = (availableDrivers || []).reduce((acc, driver) => {
          acc[driver.userId] = true;
          return acc;
        }, {} as Record<string, boolean>);
        return sort(
          activeDrivers.filter((driver) => !availableMap[driver.id]),
          sortingFunc,
        );
      }),
    );
  }

  public startEditHOS(driver: ActiveDriver) {
    if (!driver.inProgressShift) {
      return;
    }
    const shiftEnd = parseISO(driver.inProgressShift.endTimestamp);
    const minDiff = differenceInMinutes(shiftEnd, new Date());
    this.updateHOSFormGroup = new FormGroup({
      driverName: new FormControl(driver.driverName),
      driverPhone: new FormControl(driver.phoneNumber),
      shiftId: new FormControl(+driver.inProgressShift.id),
      hoursLeft: new FormControl(Math.floor(minDiff / 60) || 1, Validators.min(1)),
    });
    this.matDialog.open(this.updateHOSModal, {
      panelClass: ['w-full', 'lg:w-1/3'],
    });
  }

  public async doEditHOS() {
    const value = this.updateHOSFormGroup.value;
    if (!value.driverPhone || !value.hoursLeft) {
      return;
    }
    await this.driverService.updateHos(value.driverPhone, value.shiftId, value.hoursLeft * 60);
    this.updateHOSFormGroup = null;
  }

  public clearDate() {
    this.driverService.setDateFilter(null);
  }

  public setSortHeaderOS(key: string) {
    if (key === this.sortByOS) {
      this.sortReverseOS = !this.sortReverseOS;
    } else {
      this.sortByOS = key;
      this.sortReverseOS = false;
    }
    this.sortingFunctionOS$$.next(getSortBy(this.sortByOS, this.sortReverseOS));
  }

  public setSortHeaderUS(key: string) {
    if (key === this.sortByUS) {
      this.sortReverseUS = !this.sortReverseUS;
    } else {
      this.sortByUS = key;
      this.sortReverseUS = false;
    }
    this.sortingFunctionUS$$.next(getSortBy(this.sortByUS, this.sortReverseUS));
  }

  public setSortHeaderA(key: string) {
    if (key === this.sortByA) {
      this.sortReverseA = !this.sortReverseA;
    } else {
      this.sortByA = key;
      this.sortReverseA = false;
    }
    this.sortingFunctionA$$.next(getSortByAva(this.sortByA, this.sortReverseA));
  }

  public setSortHeaderUA(key: string) {
    if (key === this.sortByUA) {
      this.sortReverseUA = !this.sortReverseUA;
    } else {
      this.sortByUA = key;
      this.sortReverseUA = false;
    }
    this.sortingFunctionUA$$.next(getSortBy(this.sortByUA, this.sortReverseUA));
  }

  public scrollToAvailableRegion(regionId: number) {}
}

function inProgressFilter(drivers: ActiveDriver[], sortingFunc: DriverSorter): ActiveDriver[] {
  return sort(
    drivers.filter((driver) => driver.inProgressShift),
    sortingFunc,
  );
}

function upcomingShiftFilter(drivers: ActiveDriver[], sortingFunc: DriverSorter): ActiveDriver[] {
  return sort(
    drivers.filter((driver) => driver.upcomingShift),
    sortingFunc,
  );
}

function needsAttentionFilter(drivers: ActiveDriver[]): ActiveDriver[] {
  return drivers.filter(
    (driver) => driver.inProgressShift?.rejectedCount > 0 || driver.inProgressShift?.unassignedCount > 0,
  );
}

function notActionNeededFilter(drivers: ActiveDriver[]): ActiveDriver[] {
  return drivers.filter(
    (driver) =>
      (!driver.inProgressShift || (driver.inProgressShift?.rejectedCount || 0) === 0) &&
      (!driver.inProgressShift || (driver.inProgressShift?.unassignedCount || 0) === 0),
  );
}

function toRegionGroupedDrivers(drivers: ListAvailableDriversResponseDriver[]): RegionGroupedAvailableDrivers[] {
  if (!drivers || !drivers.length) {
    return [];
  }

  const regionMaps = drivers.reduce((acc, driver) => {
    if (!acc[driver.lastRegionId]) {
      acc[driver.lastRegionId] = {
        id: driver.lastRegionId,
        name: driver.lastRegionName,
        drivers: [],
      };
    }
    acc[driver.lastRegionId].drivers.push(driver);
    return acc;
  }, {} as Record<number, RegionGroupedAvailableDrivers>);

  const results: RegionGroupedAvailableDrivers[] = [];
  Object.values(regionMaps).forEach((region) => {
    results.push({
      ...region,
      drivers: sort(region.drivers, sortByName),
    });
  });

  results.sort(sortByName);

  return results;
}

function getSortBy(key: string, reverse: boolean): DriverSorter {
  const temp = sortBy(`${reverse ? '-' : ''}${key}`);
  return (a: ActiveDriver, b: ActiveDriver) => {
    return temp(a, b);
  };
}

function getSortByAva(key: string, reverse: boolean): AvaDriverSorter {
  const temp = sortBy(`${reverse ? '-' : ''}${key}`);
  return (a: ListAvailableDriversResponseDriver, b: ListAvailableDriversResponseDriver) => {
    return temp(a, b);
  };
}
